import { useContext, useEffect, useRef, useState, useSyncExternalStore, useCallback } from 'react';
import { PlaycastContext } from '../Playcast';
import { useNavigate } from 'react-router-dom';
import { MobileGamepadInput, MobileGamepadUi, useMobilePortrait } from '@playcastdotio/MobileGamepad';
import { captureStore, InputCapture, useAnimationFrame } from '@playcastdotio/InputCapture';
import { isMobileDevice } from '../../api/utility';
import { Overlay } from './overlay/Overlay';
import { mediaStreamState, playerStaticImageState, showStaticImageState } from '../../api/stores';
import { playerDisconnect } from '../../api';
import clsx from 'clsx';
import AudioDebugOverlay from './AudioDebugOverlay';

const base64ToBlob = (base64: string): Blob => {
  const byteString = atob(base64.split(',')[1]);
  const mimeString = base64.split(',')[0].split(':')[1].split(';')[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mimeString });
};

export const Player = () => {
  const data = useContext(PlaycastContext);
  const access = useSyncExternalStore(captureStore.subscribeAccess, captureStore.getAccess);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const navigate = useNavigate();
  const [isPhysicalGamepadConnected, setIsPhysicalGamepadConnected] = useState(false);
  const isMobilePortrait = useMobilePortrait();
  const showDebugOverlay = false;

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isIdle, setIsIdle] = useState(false);
  const [isToastShown, setIsToastShown] = useState(false);
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [hasAudio, setHasAudio] = useState(false);
  const [hasVideo, setHasVideo] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [imageSrc, setImageSrc] = useState<string>();

  const videoFrame = useRef<HTMLVideoElement>(null);
  const audioFrame = useRef<HTMLAudioElement>(null);
  const currentIsGamepadConnectedRef = useRef(false);

  useAnimationFrame(() => {
    const g = navigator.getGamepads();
    const hasGamepad = !!g.some((element) => element instanceof Gamepad);

    // Only update state if the value has changed
    if (hasGamepad !== currentIsGamepadConnectedRef.current) {
      currentIsGamepadConnectedRef.current = hasGamepad;
      setIsPhysicalGamepadConnected(hasGamepad);
    }

    // Check for gamepad activity
    if (hasGamepad) {
      const gamepad = g[0];
      if (gamepad) {
        // Check if any button is pressed or any axis is moved
        const hasButtonPress = gamepad.buttons.some((button) => button.pressed);
        const hasAxisMovement = gamepad.axes.some((axis) => Math.abs(axis) > 0.1);
        if (hasButtonPress || hasAxisMovement) {
          resetIdleTimeout();
        }
      }
    }
  });

  const updateStreamState = (stream: MediaStream | null, audioStream: MediaStream | null) => {
    if (stream) {
      const videoTracks = stream.getVideoTracks();
      const audioTracks = audioStream ? audioStream.getAudioTracks() : [];
      setHasVideo(videoTracks.length > 0);
      setHasAudio(audioTracks.length > 0);
      setIsVisible(videoTracks.length > 0);

      if (videoFrame.current) {
        // videoFrame.current.srcObject = new MediaStream(videoTracks);
        videoFrame.current.srcObject = stream;
        videoFrame.current.autoplay = true;
        videoFrame.current.playsInline = true;
        videoFrame.current.muted = true;
        setIsMuted(true); // Initial muted state for video

        // Add playing state listener
        videoFrame.current.onplay = () => setIsPlaying(true);
        videoFrame.current.onpause = () => setIsPlaying(false);
        videoFrame.current.onwaiting = () => setIsPlaying(false);
      }

      if (audioFrame.current && audioTracks.length > 0) {
        // audioFrame.current.srcObject = new MediaStream(audioTracks);
        // audioFrame.current.autoplay = true;
        // audioFrame.current.muted = false;
        // // Track audio mute state
        // audioFrame.current.onvolumechange = () => {
        //   if (audioFrame.current) {
        //     setIsMuted(audioFrame.current.muted);
        //   }
        // };
        // // Track audio playing state
        // audioFrame.current.onplay = () => setIsPlaying(true);
        // audioFrame.current.onpause = () => setIsPlaying(false);
        // audioFrame.current.onwaiting = () => setIsPlaying(false);
      } else {
        // setHasAudio(false);
        // if (!audioFrame.current) {
        //   console.error('[AUDIO] audioFrame.current is null');
        // }
        // if (audioTracks.length === 0) {
        //   console.error('[AUDIO] audioTracks.length is 0', audioTracks);
        // }
      }
    } else {
      setHasVideo(false);
      setHasAudio(false);
      setIsVisible(false);
      setIsPlaying(false);
      setIsMuted(true);
    }
  };

  useEffect(() => {
    // Check initial state when component mounts
    const currentState = mediaStreamState.currentState;
    if (currentState.stream || currentState.audioStream) {
      updateStreamState(currentState.stream, currentState.audioStream);
    }

    const listener = mediaStreamState.addListener((state) => {
      console.log('media stream state updated', state);
      // alert('media stream state updated');
      // alert(state.audioStream.getAudioTracks().length);
      updateStreamState(state.stream, state.audioStream);
    });

    const imageListener = playerStaticImageState.addListener((state) => {
      console.log('static image state updated', state);
      if (state.staticImageBase64) {
        const src = URL.createObjectURL(base64ToBlob(state.staticImageBase64));
        setImageSrc(src);
      }
    });
    if (playerStaticImageState.currentState.staticImageBase64) {
      const src = URL.createObjectURL(base64ToBlob(playerStaticImageState.currentState.staticImageBase64));
      setImageSrc(src);
    }

    const staticImageVisbilityListener = showStaticImageState.addListener((state) => {
      setIsVisible(!state.showStaticImage);
    });
    setIsVisible(!showStaticImageState.currentState.showStaticImage);

    return () => {
      mediaStreamState.removeListener(listener);
      playerStaticImageState.removeListener(imageListener);
      showStaticImageState.removeListener(staticImageVisbilityListener);
    };
  }, []);

  useEffect(() => {
    const unmute = () => {
      console.log('unmuting video');
      if (audioFrame.current) {
        audioFrame.current.muted = false;
        setIsMuted(false);
      }
      if (videoFrame.current) {
        videoFrame.current.muted = false;
        // play video too
        videoFrame.current.play();
        setIsMuted(false);
      }
    };
    window.addEventListener('input', unmute);
    window.addEventListener('click', unmute);
    window.addEventListener('touchstart', unmute);
    unmute();
    return () => {
      window.removeEventListener('input', unmute);
      window.removeEventListener('click', unmute);
      window.removeEventListener('touchstart', unmute);
    };
  }, []);

  useEffect(() => {
    if (isVisible) {
      videoFrame.current?.style.setProperty('display', 'block');
      // videoFrame.current?.play();
    } else {
      videoFrame.current?.style.setProperty('display', 'none');
      // videoFrame.current?.pause();
    }
  }, [isVisible]);

  const handleExitStream = () => {
    playerDisconnect();
  };

  console.log('imageSrc', imageSrc);

  const resetIdleTimeout = useCallback(() => {
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
    }
    setIsIdle(false);
    idleTimeoutRef.current = setTimeout(() => {
      setIsIdle(true);
    }, 3000); // 3 seconds
  }, []);

  const checkFullscreen = useCallback(() => {
    const isNowFullscreen = !!document.fullscreenElement;
    setIsFullscreen(isNowFullscreen);

    if (isNowFullscreen) {
      resetIdleTimeout();
    }
  }, [resetIdleTimeout]);

  const handleFullscreenChange = useCallback(() => {
    checkFullscreen();
  }, [checkFullscreen]);

  const handleUserActivity = useCallback(() => {
    resetIdleTimeout();
  }, [resetIdleTimeout]);

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('mousedown', handleUserActivity);
    window.addEventListener('keypress', handleUserActivity);
    window.addEventListener('touchstart', handleUserActivity);

    // Initial fullscreen check
    checkFullscreen();
    resetIdleTimeout();

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('mousedown', handleUserActivity);
      window.removeEventListener('keypress', handleUserActivity);
      window.removeEventListener('touchstart', handleUserActivity);

      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
    };
  }, [checkFullscreen, resetIdleTimeout, handleFullscreenChange, handleUserActivity]);

  return (
    <>
      {data.mode.mode === 'player' && <InputCapture skipGamepad={isMobileDevice() && !isPhysicalGamepadConnected} />}
      {isMobileDevice() && access.gamepad && data.mode.mode === 'player' ? (
        <>
          {!isPhysicalGamepadConnected && <MobileGamepadInput />}
          <MobileGamepadUi disconnect={handleExitStream} isPhysicalGamepadConnected={isPhysicalGamepadConnected} />
        </>
      ) : null}
      <div className='absolute flex h-dvh w-full items-end overflow-hidden'>
        {/* Custom ESC notification */}
        {isFullscreen && isIdle && !isMobileDevice() && (
          <div
            id='fullscreen-notification'
            data-testid='fullscreen-notification'
            className={clsx(
              'fixed left-1/2 top-0 z-50 mt-8 -translate-x-1/2',
              'transform-gpu',
              '[transform:translate(-50%,0)]',
              'animate-notification-slide-down',
              'bg-primary rounded-lg px-4 py-2',
              'shadow-lg shadow-black/10',
            )}
          >
            <span id='fullscreen-notification-text' data-testid='fullscreen-notification-text' className='font-semibold text-gray-900'>
              Hold ESC to exit fullscreen
            </span>
          </div>
        )}
        <div
          className={clsx(
            isMobilePortrait ? 'right-0 top-full h-[100vw] w-[100dvh] origin-top-right rotate-90 overflow-hidden' : 'h-[100dvh] w-[100vw]',
            'bg-content absolute flex items-center justify-center',
          )}
        >
          <Overlay />
          {showDebugOverlay && (
            <div className='debugOverlay absolute left-0 top-0 z-20 bg-black bg-opacity-50 p-2 text-white'>
              {/* has audio track? */}
              <div>Has Audio: {hasAudio ? 'Yes' : 'No'}</div>
              {/* has video track? */}
              <div>Has Video: {hasVideo ? 'Yes' : 'No'}</div>
              {/* is muted? */}
              <div>Is Muted: {isMuted ? 'Yes' : 'No'}</div>
              {/* is playing? */}
              <div>Is Playing: {isPlaying ? 'Yes' : 'No'}</div>
              {/* is visible? */}
              <div>Is Visible: {isVisible ? 'Yes' : 'No'}</div>
              <div className='z-30 mt-4 flex w-full justify-center border-t border-gray-600 pt-2'>
                <AudioDebugOverlay audioRef={audioFrame} />
              </div>
            </div>
          )}

          <video
            ref={videoFrame}
            className={clsx(
              'bg-content max-w-screen absolute z-10 aspect-video max-h-screen w-full object-contain',
              isVisible ? 'visible' : 'hidden',
            )}
            hidden={!isVisible}
            autoPlay
            muted={false}
            // controls
            onContextMenu={(e) => {
              e.preventDefault();
              return false;
            }}
          ></video>
          {/* <audio ref={audioFrame} className='z-50' autoPlay></audio> */}
          {!isVisible && (
            <div className='bg-content max-w-screen absolute flex h-full max-h-screen w-full items-center justify-center'>
              <img src={imageSrc ? imageSrc : 'images/global-frame.png'} alt='static background' className='h-full w-full object-cover' />
            </div>
          )}
        </div>
      </div>
    </>
  );
};
